import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { EyeOff, Timer, MessageSquareWarning } from "lucide-react";
import { FadeUp, ChapterHeading } from "./Shared";

const problems = [
  {
    icon: EyeOff,
    title: "Footfall is not feeling",
    body: "Counters tell you how many walked in. They never tell you how the floor felt at 6pm on a Saturday.",
  },
  {
    icon: Timer,
    title: "Surveys arrive too late",
    body: "Weeks to compile, answered by whoever bothered. By the time you read the report, the moment is gone.",
  },
  {
    icon: MessageSquareWarning,
    title: "Reviews are the vocal few",
    body: "Online ratings come from a loud minority — not the real crowd standing inside your venue right now.",
  },
];

export function Problem() {
  return (
    <section id="problem" className="py-28 lg:py-36" data-testid="section-problem">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        <ChapterHeading number="01" title="THE PROBLEM" />
        <div className="grid lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-5">
            <p className="font-display font-bold text-3xl lg:text-4xl leading-tight text-white">
              Physical venues run on <span className="text-zinc-500 line-through decoration-[#FF2D55] decoration-2">gut feel</span>.
            </p>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
              Layouts, staffing, events, tenant mix — million-rupee decisions made blind. The right presentation, the right
              use and a timely update on audience sentiment is a game changer. Most venues never get it.
            </p>
          </FadeUp>
          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {problems.map((p, i) => (
              <FadeUp key={p.title} delay={i * 0.12} className="bg-[#0a0a0a] p-8 group hover:bg-[#101010] transition-colors duration-300">
                <p.icon size={22} className="text-[#E5FF00] mb-6" strokeWidth={1.5} />
                <h3 className="font-display font-bold text-lg mb-3">{p.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{p.body}</p>
                <p className="mt-6 font-mono-tech text-[10px] tracking-[0.3em] text-zinc-600 group-hover:text-[#E5FF00] transition-colors">
                  BLINDSPOT_0{i + 1}
                </p>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const ecosystemNodes = [
  { label: "MOBILE APP", desc: "Your audience already lives on LyPinn's hyper-local map — 3 km radius, realtime, verified.", tag: "NODE_A" },
  { label: "PULSEPOINTS — PRINT", desc: "Elegant print touchpoints placed exactly where your audience stands. Any phone camera engages. No app needed.", tag: "NODE_B" },
  { label: "PULSESCREENS — KIOSK", desc: "Your existing screens become one-tap sentiment touchpoints in your highest-dwell zones.", tag: "NODE_C" },
];

export function Ecosystem() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const y2 = useTransform(scrollYProgress, [0, 1], [120, -120]);

  return (
    <section id="ecosystem" ref={ref} className="py-28 lg:py-36 bg-[#080808] border-y border-white/10 overflow-hidden" data-testid="section-ecosystem">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        <ChapterHeading number="02" title="THE ECOSYSTEM" />
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6 space-y-px">
            {ecosystemNodes.map((n, i) => (
              <FadeUp key={n.tag} delay={i * 0.1}>
                <div className="flex gap-6 p-8 border border-white/10 bg-[#0a0a0a] hover:border-[#E5FF00]/40 transition-colors duration-300 group">
                  <span className="font-mono-tech text-[10px] tracking-[0.3em] text-zinc-600 group-hover:text-[#E5FF00] transition-colors pt-1.5 shrink-0">
                    {n.tag}
                  </span>
                  <div>
                    <h3 className="font-display font-bold text-xl mb-2">{n.label}</h3>
                    <p className="text-sm text-zinc-500 leading-relaxed">{n.desc}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
            <FadeUp delay={0.35}>
              <p className="pt-8 text-zinc-400 leading-relaxed max-w-lg">
                Three capture surfaces, one stream. Every signal is first-party, location-verified and timestamped —{" "}
                <span className="text-white">a cohesive ecosystem, not another dashboard of guesses.</span>
              </p>
            </FadeUp>
          </div>
          <div className="lg:col-span-6 relative hidden lg:block">
            <motion.div style={{ y: y1 }} className="relative z-10 w-3/4 ml-auto">
              <div className="overflow-hidden border border-white/15" style={{ clipPath: "polygon(12% 0, 100% 0, 100% 100%, 0 100%, 0 12%)" }}>
                <img
                  src="https://images.pexels.com/photos/15467758/pexels-photo-15467758.jpeg?auto=compress&cs=tinysrgb&w=940"
                  alt="Hand scanning a PulsePoint with a smartphone"
                  className="w-full h-[340px] object-cover"
                  data-testid="ecosystem-image-qr"
                />
              </div>
              <p className="mt-3 font-mono-tech text-[10px] tracking-[0.25em] text-zinc-600 text-right">PULSEPOINT — TOUCH ENGAGED</p>
            </motion.div>
            <motion.div style={{ y: y2 }} className="relative z-0 w-2/3 -mt-16">
              <div className="overflow-hidden border border-white/15" style={{ clipPath: "polygon(0 0, 100% 0, 100% 88%, 88% 100%, 0 100%)" }}>
                <img
                  src="https://images.pexels.com/photos/14084245/pexels-photo-14084245.jpeg?auto=compress&cs=tinysrgb&w=940"
                  alt="Illuminated PulseScreen kiosks in a dark venue"
                  className="w-full h-[280px] object-cover"
                  data-testid="ecosystem-image-kiosk"
                />
              </div>
              <p className="mt-3 font-mono-tech text-[10px] tracking-[0.25em] text-zinc-600">PULSESCREEN — CONCOURSE</p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
